// Contrat des templates : types et JSON Schema. La validation (ajv et contrôles sémantiques que
// le schéma ne sait pas exprimer) est dans `validate.ts` : tout template passe par
// `validateTemplate` avant le moteur.
import type { Season } from './dates.ts';
import { HOURLY_VARIABLE_IDS, VARIABLE_IDS, type HourlyVariableId, type VariableId } from './variables.ts';

export const CATEGORIES = ['champignons', 'peche', 'jardin'] as const;
/** Pêche : un profil compose une espèce et, au choix, un poste et une esche (règles en ET). */
export const ROLES = ['species', 'poste', 'esche'] as const;
export const MAX_SLOT_HOURS = 24;
export const AGGREGATIONS = ['sum', 'avg', 'min', 'max', 'delta', 'count_where', 'consecutive'] as const;
export const OPERATORS = ['>=', '<=', 'between', 'outside'] as const;
/** Agrégations qui comptent des jours remplissant la condition `where`. */
export const COUNTING_AGGREGATIONS = ['count_where', 'consecutive'] as const;
export const MAX_RULE_DAYS = 30;
export const MAX_DELAY_DAYS = 60;
export const MAX_EXTENDS_DEPTH = 5;
export const MAX_HINT_LABEL = 120;
export const DEFAULT_SOFT_THRESHOLD = 0.5;

export type Category = (typeof CATEGORIES)[number];
export type Aggregation = (typeof AGGREGATIONS)[number];
export type Operator = (typeof OPERATORS)[number];
export type TemplateRole = (typeof ROLES)[number];

export interface Condition {
  op: Operator;
  value: number | [number, number];
}

export interface Rule extends Condition {
  /** Identifiant facultatif : une règle d'un enfant remplace celle du parent de même id. */
  id?: string;
  var: VariableId;
  agg: Aggregation;
  /** Période d'agrégation, en jours, finissant le jour évalué (inclus). */
  days: number;
  /** Seul `daily` : les créneaux horaires passent par `hourly` (conditions heure par heure). */
  resolution?: 'daily';
  /** Condition journalière, obligatoire pour `count_where` et `consecutive`. */
  where?: Condition;
  type: 'hard' | 'soft';
  /** Poids d'une règle `soft` (1 par défaut). */
  weight?: number;
  /** Espèce de pêche : règle gardée seulement si le profil est composé avec l'un de ces postes (slugs). */
  when_poste?: string[];
}

/**
 * Indice affiché à côté d'une fenêtre (« Fortes pluies récentes ») : mêmes briques qu'une règle,
 * sans effet sur l'ouverture, la fermeture ni les notifications.
 */
export interface Hint extends Condition {
  /** Un indice d'un enfant remplace celui du parent de même id. */
  id: string;
  var: VariableId;
  agg: Aggregation;
  days: number;
  where?: Condition;
  /** Texte affiché ; `{mesure}` y est remplacé par la valeur mesurée (« 85 mm sur 10 j »). */
  label: string;
}

/** Condition vérifiée à chaque heure d'un créneau (poste ou esche de pêche). */
export interface HourlyCondition extends Condition {
  id?: string;
  var: HourlyVariableId;
  /** Paramètre du profil qui remplace `value` (ex. limite de vent choisie pour son bateau). */
  param?: string;
}

export interface TemplateDefinition {
  slug: string;
  name: string;
  category: Category;
  extends?: string;
  /** Parent seulement : saison et délai facultatifs, jamais évalué seul. */
  abstract?: boolean;
  season?: Season;
  rules: Rule[];
  /** Indices affichés à côté d'une fenêtre, sans effet sur son calcul. */
  hints?: Hint[];
  /** Fenêtre = [déclenchement + min, déclenchement + max], bornes incluses. */
  delay_days?: [number, number];
  /** Part minimale (0 à 1) du poids des règles `soft` satisfaites. */
  soft_threshold?: number;
  /** Pêche : espèce (évaluable seule), poste ou esche (composés avec une espèce). */
  role?: TemplateRole;
  /** Poste ou esche à créneaux : conditions heure par heure, tenues au moins `min_hours` heures d'affilée. */
  hourly?: HourlyCondition[];
  /** Esche : un créneau contient au moins une heure où toutes ces conditions sont vraies (aube, crépuscule). */
  anchor?: HourlyCondition[];
  min_hours?: number;
  /** Plage horaire locale des créneaux, [début, fin) en heures (ex. [6, 21] : de jour). */
  hour_range?: [number, number];
  /** Poste de navigation (kayak, bateau) : aide à la décision, renvoi au bulletin côtier. */
  navigation?: boolean;
}

const conditionValue = {
  if: { properties: { op: { enum: ['between', 'outside'] } } },
  then: { properties: { value: { type: 'array', items: { type: 'number' }, minItems: 2, maxItems: 2 } } },
  else: { properties: { value: { type: 'number' } } },
};

const hourlyCondition = {
  type: 'object',
  required: ['var', 'op', 'value'],
  additionalProperties: false,
  properties: {
    id: { type: 'string', pattern: '^[a-z0-9][a-z0-9-]{0,39}$' },
    var: { enum: HOURLY_VARIABLE_IDS },
    op: { enum: OPERATORS },
    value: {},
    param: { type: 'string', pattern: '^[a-z][a-z0-9_]{0,39}$' },
  },
  ...conditionValue,
};

/** `where` obligatoire pour count_where et consecutive, interdit sinon (règles et indices). */
const aggregationCondition = { if: { properties: { agg: { enum: COUNTING_AGGREGATIONS } } }, then: { required: ['where'] }, else: { not: { required: ['where'] } } };

const condition = {
  type: 'object',
  required: ['op', 'value'],
  additionalProperties: false,
  properties: { op: { enum: OPERATORS }, value: {} },
  ...conditionValue,
};

export const TEMPLATE_JSON_SCHEMA = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  $id: 'https://crenow.quentissimo.fr/schemas/template.json',
  title: 'Template Crénow',
  type: 'object',
  required: ['slug', 'name', 'category', 'rules'],
  additionalProperties: false,
  properties: {
    slug: { type: 'string', pattern: '^[a-z0-9][a-z0-9-]{1,79}$' },
    name: { type: 'string', minLength: 1, maxLength: 100 },
    category: { enum: CATEGORIES },
    extends: { type: 'string', pattern: '^[a-z0-9][a-z0-9-]{1,79}$' },
    abstract: { type: 'boolean' },
    season: {
      type: 'object',
      required: ['from', 'to'],
      additionalProperties: false,
      properties: { from: { type: 'string', pattern: '^\\d{2}-\\d{2}$' }, to: { type: 'string', pattern: '^\\d{2}-\\d{2}$' } },
    },
    rules: {
      type: 'array',
      items: {
        type: 'object',
        required: ['var', 'agg', 'days', 'op', 'value', 'type'],
        additionalProperties: false,
        properties: {
          id: { type: 'string', pattern: '^[a-z0-9][a-z0-9-]{0,39}$' },
          var: { enum: VARIABLE_IDS },
          agg: { enum: AGGREGATIONS },
          days: { type: 'integer', minimum: 1, maximum: MAX_RULE_DAYS },
          resolution: { const: 'daily' },
          where: condition,
          op: { enum: OPERATORS },
          value: {},
          type: { enum: ['hard', 'soft'] },
          weight: { type: 'number', exclusiveMinimum: 0, maximum: 100 },
          when_poste: { type: 'array', items: { type: 'string', pattern: '^[a-z0-9][a-z0-9-]{1,79}$' }, minItems: 1 },
        },
        allOf: [conditionValue, aggregationCondition],
      },
    },
    hints: {
      type: 'array',
      items: {
        type: 'object',
        required: ['id', 'var', 'agg', 'days', 'op', 'value', 'label'],
        additionalProperties: false,
        properties: {
          id: { type: 'string', pattern: '^[a-z0-9][a-z0-9-]{0,39}$' },
          var: { enum: VARIABLE_IDS },
          agg: { enum: AGGREGATIONS },
          days: { type: 'integer', minimum: 1, maximum: MAX_RULE_DAYS },
          where: condition,
          op: { enum: OPERATORS },
          value: {},
          label: { type: 'string', minLength: 1, maxLength: MAX_HINT_LABEL },
        },
        allOf: [conditionValue, aggregationCondition],
      },
    },
    delay_days: { type: 'array', items: { type: 'integer', minimum: 0, maximum: MAX_DELAY_DAYS }, minItems: 2, maxItems: 2 },
    soft_threshold: { type: 'number', minimum: 0, maximum: 1 },
    role: { enum: ROLES },
    hourly: { type: 'array', items: hourlyCondition },
    anchor: { type: 'array', items: hourlyCondition },
    min_hours: { type: 'integer', minimum: 1, maximum: MAX_SLOT_HOURS },
    hour_range: { type: 'array', items: { type: 'integer', minimum: 0, maximum: 24 }, minItems: 2, maxItems: 2 },
    navigation: { type: 'boolean' },
  },
} as const;

export interface TemplateIssue {
  /** Chemin JSON Pointer (`/rules/0/value`), vide pour la racine. */
  path: string;
  message: string;
}

export const POSTE_ONLY = 'Réservé aux postes de pêche (« role »: « poste »)';
export const ESCHE_ONLY = 'Réservé aux esches de pêche (« role »: « esche »)';
export const SPECIES_ONLY = 'Réservé aux espèces de pêche (« role »: « species »)';
export const SLOT_ONLY = 'Réservé aux postes et esches de pêche (« role »: « poste » ou « esche »)';
/** Poste et esche ne pondèrent rien : leurs règles `soft` dilueraient le seuil de l'espèce. */
export const NO_SOFT = 'Un poste ou une esche n’a que des règles « hard » (le seuil « soft » est celui de l’espèce)';

/** Rôles composés avec une espèce (ni évaluables seuls ni proposés comme espèce). */
export const isAddOnRole = (role: TemplateRole | null | undefined): role is 'poste' | 'esche' => role === 'poste' || role === 'esche';
